import { TextField } from "@mui/material";
import { SyncAltOutlined } from "@mui/icons-material";
import { useState, useCallback, useEffect } from "react";
import { Text, InfoContainer } from "$components";
import { DeliveryType, ShippingRecord } from "$types";
import { getShippingRecordDeliveryTypeLabel } from "$utils/typeConstants";
import { CompanyServices } from "$services";
import { queryClient } from "$utils/queryClient";
import { CacheInvalidationAction } from "$utils/cache";

type Props = {
  classes: any;
  shippingRecord: ShippingRecord | object;
};

function ShippingRecordPublicInfoBlock({ classes, shippingRecord }: Props) {
  const {
    deliveryType,
    carrierReference,
    commissionCompanyId,
    carrierCompanyId,
    agencyCompanyId,
  } = shippingRecord as ShippingRecord;

  const [companies, setCompanies] = useState([]);

  const fecthCompanies = useCallback(async () => {
    const listId = [];
    if (commissionCompanyId) {
      listId.push(commissionCompanyId);
    }
    if (carrierCompanyId) {
      listId.push(carrierCompanyId);
    }
    if (agencyCompanyId) {
      listId.push(agencyCompanyId);
    }
    if (listId.length > 0) {
      const filters = {
        $inId: listId,
      };
      let companies;
      companies = await queryClient.getQueryData([
        CacheInvalidationAction.GET_COMPANIES,
        filters,
      ]);
      if (!companies) {
        companies = await queryClient.fetchQuery(
          [CacheInvalidationAction.GET_COMPANIES, filters],
          () => CompanyServices.getPublicCompanies(filters),
          { staleTime: 60 * 1000 }
        );
        companies = companies.data;
        await queryClient.setQueryData(
          [CacheInvalidationAction.GET_COMPANIES, filters],
          companies
        );
      }
      setCompanies(companies);
    }
  }, [commissionCompanyId, carrierCompanyId, agencyCompanyId]);

  useEffect(() => {
    fecthCompanies();
  }, [fecthCompanies]);

  const isCommissionCompanySelectDisplayed =
    deliveryType && deliveryType === DeliveryType.CHARTERING;
  const isCarrierCompanySelectDisplayed =
    deliveryType && deliveryType !== DeliveryType.PROVISIONING;
  const isAgencyCompanySelectDisplayed =
    deliveryType &&
    deliveryType !== DeliveryType.CHARTERING &&
    deliveryType !== DeliveryType.PROVISIONING;

  return (
    <div>
      <InfoContainer
        className={classes.paperContainer}
        leftHeader={
          <div className={classes.infoHeader}>
            <SyncAltOutlined />
            <Text type="h3">Informations suivi</Text>
          </div>
        }
      >
        <>
          <div className={classes.lineContainer}>
            {deliveryType !== DeliveryType.PROVISIONING ? (
              <div className={classes.inputContainer}>
                <TextField
                  style={{ width: "100%" }}
                  label="Ref transporteur"
                  disabled
                  variant="outlined"
                  value={carrierReference}
                  InputLabelProps={{
                    shrink: Boolean(deliveryType),
                  }}
                />
              </div>
            ) : (
              <>
                <div className={classes.inputContainer} />
                <div className={classes.inputContainer} />
              </>
            )}
          </div>
          <div className={classes.lineContainer}>
            <div className={classes.inputContainer}>
              <TextField
                style={{ width: "100%" }}
                label="Type d'envoi"
                disabled
                variant="outlined"
                InputLabelProps={{
                  shrink: Boolean(deliveryType),
                }}
                value={getShippingRecordDeliveryTypeLabel(deliveryType)}
              />
            </div>
            {isCommissionCompanySelectDisplayed && (
              <div className={classes.inputContainer}>
                <TextField
                  style={{ width: "100%" }}
                  label="Commissionnaire"
                  disabled
                  variant="outlined"
                  value={
                    companies?.find(
                      (company) => commissionCompanyId === company._id
                    )?.name
                  }
                  InputLabelProps={{
                    shrink: !!commissionCompanyId,
                  }}
                />
              </div>
            )}
            {isCarrierCompanySelectDisplayed && (
              <div className={classes.inputContainer}>
                <TextField
                  style={{ width: "100%" }}
                  label="Transporteur"
                  disabled
                  variant="outlined"
                  value={
                    companies?.find(
                      (company) => carrierCompanyId === company._id
                    )?.name
                  }
                  InputLabelProps={{
                    shrink: !!carrierCompanyId,
                  }}
                />
              </div>
            )}
            {isAgencyCompanySelectDisplayed && (
              <div className={classes.inputContainer}>
                <TextField
                  style={{ width: "100%" }}
                  label="Agence"
                  disabled
                  variant="outlined"
                  value={
                    companies?.find(
                      (company) => agencyCompanyId === company._id
                    )?.name
                  }
                  InputLabelProps={{
                    shrink: !!agencyCompanyId,
                  }}
                />
              </div>
            )}
          </div>
        </>
      </InfoContainer>
    </div>
  );
}

export default ShippingRecordPublicInfoBlock;
